import {
  ACTING_AS_COMPANY_ON_OWN_CONTENT,
  COMPANY_IS_INACTIVE,
  NOT_ACTING_AS_COMPANY,
} from "@/lib/genesis-error-codes";
import { hasErrorCode } from "@/pages/company/company-admin-error";
// Pure helpers + shared types for SHARE items. Kept separate from the
// component file so Vite's Fast Refresh isn't disabled by mixed exports.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ShareItemLike = any;

/**
 * Whether the caller is acting AS the company one subject is filed under.
 *
 * <b>The client's own half of the v23 predicate.</b> genesis publishes nothing for it — the acting
 * context lives in spark and genesis only checks the `actingAsCompanyId` a write carries — so the two
 * halves come from different places and are ANDed here rather than anywhere genesis can be asked.
 *
 * The `Boolean(actingAsCompanyId)` guard is load-bearing rather than defensive: without it a subject
 * whose `company` carries no id would match a caller acting as nobody, `undefined === undefined`, and
 * the narrowing would silently invert for exactly the malformed row it is least able to judge.
 */
const actingAsOwningCompany = (
  subject: ShareItemLike,
  actingAsCompanyId: string | null,
): boolean => Boolean(actingAsCompanyId) && subject.company.id === actingAsCompanyId;

/**
 * Whether the caller stands BEHIND this item — its author for a personal one, an admin of the
 * publishing company for a company one.
 *
 * <b>What it is for: the affordances that are withheld from the publishing side.</b> Applying to a
 * project and rating an item are for everybody EXCEPT the people the item speaks for, so those read
 * this negated. It is deliberately NOT {@link mayManageItem} any more, and the split is concept v23's
 * doing: v23 narrowed editing and deleting to a caller whose acting CONTEXT matches the item, and left
 * rating and signing up exactly as they were. One predicate serving both would have widened them by
 * the same stroke — the star rating appearing on an admin's own company post the moment they left the
 * context, and on their own personal post the moment they entered one. Both are controls genesis
 * refuses and neither is something v23 asked to appear.
 *
 * <b>An either/or, never an OR.</b> `owner` answers "did I write this" and is derived from
 * `person_id`, which nothing ever rewrites. For a COMPANY item that makes it useless as a
 * permission: the admin who wrote a post keeps `owner === true` on it after being demoted to member
 * or removed from the company, while `isAdminOfCompany` correctly turns false. `owner ||
 * isAdminOfCompany` would therefore offer that person an Edit button genesis refuses, because for a
 * company item genesis consults membership alone and never looks at who wrote it.
 *
 * So: a personal item asks `owner`, a company item asks `isAdminOfCompany`, and neither borrows from
 * the other. Concept v9, the company account model — "Autorschaft ist bei
 * Firmen-Inhalten KEIN Recht, auch nicht für die Person, die es geschrieben hat".
 *
 * <b>A hint, not a gate.</b> genesis decides every one of these for itself; this only says what the
 * UI bothers to offer.
 */
export const answersForItem = (item: ShareItemLike): boolean =>
  item?.company ? Boolean(item.isAdminOfCompany) : Boolean(item?.owner);

/**
 * Whether the caller may EDIT or DELETE this item — {@link answersForItem} plus, in BOTH arms,
 * concept v23's second conjunct.
 *
 * <b>THE DECLARED CONTEXT AND THE OWNERSHIP OF THE ITEM MUST MATCH</b>
 * (the company account model). One rule with two directions, not two rules:
 * a company item is managed only while acting as THAT company, and a personal item only while acting
 * as NOBODY. "Der Kontext ist damit ausschliesslich: zwei Modi, jeder mit seinem eigenen Gegenstand,
 * und keiner greift in den anderen."
 *
 * <b>The company arm</b> (Kevin, 2026-09-11) asks a current admin membership AND that the viewer is
 * acting in that company's context. Punkt 6 had said since v7 that "JEDER Admin darf JEDEN Firmen-Post
 * bearbeiten und löschen"; from v23 they do it in the company's name or not at all. An admin browsing
 * as themselves is therefore offered nothing on their own company's work — and the switch is one
 * click away, so nothing becomes unreachable.
 *
 * <b>The personal arm is the MIRROR</b> (Kevin, 2026-09-12: "if someone acts as a company, he cannot
 * edit/delete/create comments as private person"). Edit and Delete leave a caller's OWN post while any
 * company context is active, whichever company it is: the item belongs to no company, so no
 * declaration can agree with it and genesis refuses every one of them with GENESIS001260. An earlier
 * revision of this note said a personal item "never reads the context at all"; that was true until
 * 2026-09-12 and is false now. The friction is deliberate and the concept prices it: "Wer im
 * Firmen-Kontext einen Tippfehler im eigenen privaten Kommentar sieht, muss den Kontext erst
 * verlassen."
 *
 * <b>Authorship is still not a right</b> — the v8/v16 orphaning rule is untouched. What changed is
 * the CONTEXT the right is exercised in, not who holds it.
 *
 * <b>A hint, not a gate.</b> genesis enforces every write against the item's stored `company_id`
 * (`validateMayWriteSharedItem`); this only decides what the UI bothers to offer. And the context half
 * is not a permission either: the declaration is client-sent, so v23 guards against acting in the
 * WRONG context and against nothing else (genesis#300). Nobody may cite it as an access boundary.
 *
 * @param actingAsCompanyId the company the viewer is currently acting as, or null for personal
 *                          context — the RESOLVED id from `useCompanyContext`, never the raw stored
 *                          one, so an id with no admin membership behind it offers nothing.
 */
export const mayManageItem = (item: ShareItemLike, actingAsCompanyId: string | null): boolean =>
  item?.company
    ? answersForItem(item) && actingAsOwningCompany(item, actingAsCompanyId)
    : answersForItem(item) && !actingAsCompanyId;

/**
 * Whether the caller may edit or delete this COMMENT — {@link mayManageItem}'s either/or, asked of a
 * comment instead of an item.
 *
 * <b>The two cases are answered by different fields on purpose.</b> A PERSONAL comment belongs to
 * whoever wrote it, so `mine` decides. A COMPANY comment belongs to the company, so the current
 * admin membership decides and authorship is irrelevant — every current admin may change it, the
 * demoted author may not. Concept v16 (the company account model) states it
 * as `company == null ? mine : isAdminOfCompany`, and v8 explains why it is an either/or: `person_id`
 * is never rewritten, so `mine` stays true for the author of a company comment after they lose the
 * membership, while `isAdminOfCompany` correctly turns false. A `mine || isAdminOfCompany` would
 * therefore offer that person Edit and Delete that genesis refuses.
 *
 * <b>SINCE CONCEPT v23 BOTH ARMS ASK A SECOND THING</b> (0002 §6), and it is the SAME thing from two
 * sides: the declared context and the ownership of the comment must MATCH. The predicate is
 * `company == null ? (mine && !actingAsAnyCompany) : (isAdminOfCompany && actingAsThisCompany)`.
 *
 * <b>The company arm</b> (Kevin, 2026-09-11) came from his own case — "although i am a company admin
 * i can still edit the comment made by another company admin acted as company admin while i did not
 * act as company." It reverses an alternative
 * @adr SPARK-0043-a-comment-can-be-written-as-the-company@3 had rejected for "hiding a button the
 * server allows": from v23 the server does not allow it either.
 *
 * <b>The personal arm is its mirror</b> (Kevin, 2026-09-12: "if someone acts as a company, he cannot
 * edit/delete/create comments as private person"). Whoever is acting as Acme does not reach past it to
 * their own words: Edit and Delete leave a personal comment while ANY company context is active, and
 * genesis refuses such a write with GENESIS001260. This note said until 2026-09-12 that a personal
 * comment "never reads the context"; that sentence is gone rather than softened.
 *
 * <b>Authorship is still not a right.</b> Every current admin may still change every one of their
 * company's comments (v16, and the orphaning rule v8 built it against); they switch context first,
 * and the switch is open to all of them.
 *
 * <b>A hint, not a gate.</b> genesis enforces every write against the comment's stored `company_id`
 * (`validateMayWriteComment`); this only decides what the UI bothers to offer. The context half is
 * not a permission either — the declaration is client-sent, so it narrows and never grants
 * (genesis#300).
 *
 * @param actingAsCompanyId the company the viewer is currently acting as, or null for personal
 *                          context — the RESOLVED id from `useCompanyContext`.
 */
export const mayManageComment = (
  comment: ShareItemLike,
  mine: boolean,
  actingAsCompanyId: string | null,
): boolean =>
  comment?.company
    ? Boolean(comment.isAdminOfCompany) && actingAsOwningCompany(comment, actingAsCompanyId)
    : Boolean(mine) && !actingAsCompanyId;

/**
 * Send the resolved acting context unchanged. Content ownership determines whether genesis
 * accepts the write, never whether Spark declares its mode. In particular, dropping the context
 * on personal content would bypass GENESIS001260 on an already-open edit page.
 */
export const actingAsDeclaration = (
  actingAsCompanyId: string | null,
): { actingAsCompanyId?: string } =>
  actingAsCompanyId ? { actingAsCompanyId } : {};

/**
 * The user-facing sentence for a frozen company's content, or null when the failure is something
 * else.
 *
 * <b>Why this exists rather than falling through to the generic message.</b> genesis classifies the
 * refusal as VALIDATION, which `userErrorMessage` has no branch for, so without this every attempt to
 * touch a dormant company's content read "Something went wrong. Please try again." — advice that is
 * both wrong and unactionable, since trying again is exactly what will not help. The concept names
 * the requirement: the code "must NOT be collapsed into the generic authorisation refusal", because
 * the caller who sees it IS an admin and the repair is to reactivate the company.
 */
export const companyFrozenMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, COMPANY_IS_INACTIVE)
    ? intl.formatMessage({
        id: "company.inactive.frozen",
        defaultMessage:
          "This company is inactive, so its content is frozen. Reactivate the company to publish or change anything in its name.",
      })
    : null;

/**
 * The same refusal, for somebody who CANNOT repair it.
 *
 * <b>Why there are two of these.</b> GENESIS001250 reaches two very different callers. An admin of
 * the company meets it on create, edit and delete, and for them "reactivate the company" is the
 * repair — {@link companyFrozenMessage} says so, and must keep saying so. A reader meets the same
 * code on sign-up and rating, and telling them to reactivate a company they have no rights over is
 * an instruction they cannot follow: it reads as the product blaming them for a state somebody else
 * chose. They get the fact and no instruction, which is all there is for them to do with it.
 *
 * <p>The distinction is the CALLER's, not the error's — genesis raises one code, and it is right to,
 * because the cause is one thing. Which sentence to show is a question about who is asking, so it is
 * answered at the call site rather than by inspecting the error further.
 */
export const companyFrozenReaderMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, COMPANY_IS_INACTIVE)
    ? intl.formatMessage({
        id: "company.inactive.frozenReader",
        defaultMessage:
          "This company is inactive, so its content is frozen.",
      })
    : null;

/**
 * The sentence for a write refused because the caller was not acting as the owning company, or null
 * when the failure is something else.
 *
 * <b>Why the affordance being hidden is not enough.</b> {@link mayManageItem} and
 * {@link mayManageComment} withhold Edit and Delete while the viewer is in personal context, so this
 * refusal is not the button being pressed anyway — it is the PAGE that was rendered before the
 * context changed. The acting context lives only in memory and dies with the tab, so a page left
 * open can believe it is still the company after the context is gone, and concept v23 asks for a
 * usable answer there in as many words: "der Handlungs-Kontext lebt nur im Speicher und stirbt mit
 * dem Tab, also kann eine offene Seite veralten, und dann muss der Server brauchbar antworten statt
 * ‚keine Berechtigung'".
 *
 * <b>It names the repair, not the fault</b>, and the repair is inside spark — switch into the
 * company and try again. That is what keeps it apart from the other two refusals on the same writes:
 * {@link companyFrozenMessage} sends the reader to reactivate the company, and the permission refusal
 * sends them to ask for admin rights. The caller here already administers the company and the company
 * is perfectly active, so either of the other two sentences would send them somewhere that cannot
 * help.
 */
export const notActingAsCompanyMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, NOT_ACTING_AS_COMPANY)
    ? intl.formatMessage({
        id: "company.context.notActingAsCompany",
        defaultMessage:
          "This content belongs to a company, so changing it means acting as that company. Open the company page and choose “Act as this company”, then try again.",
      })
    : null;

/**
 * The MIRROR sentence: a write refused because the caller declared a company context over content
 * that is their OWN, or null when the failure is something else.
 *
 * <b>The other direction of one rule</b> (concept v23, extended by Kevin on 2026-09-12: "if someone
 * acts as a company, he cannot edit/delete/create comments as private person"). The declared context
 * and the ownership of the content must match; {@link notActingAsCompanyMessage} answers the mismatch
 * where the content carries a company, and this one answers it where the content carries none.
 *
 * <b>Two sentences rather than one, because the repairs are OPPOSITE.</b> 001259 is repaired by
 * switching INTO a company and 001260 by LEAVING one, so answering either with the other's wording
 * sends the reader further from the write they are attempting rather than nearer to it. That is also
 * why genesis gave the second direction its own code instead of reusing the first.
 *
 * <b>It names the control by the label it actually carries</b> — "Leave company context", the
 * `company.context.leave` button in the context bar — so a reader can search the screen for the words
 * they were given. And it is NOT a permission refusal: this caller owns the content and may write it,
 * they are standing in the wrong mode (genesis#300).
 *
 * <b>Why the hidden affordance is not enough</b>, exactly as for its mirror: {@link mayManageItem} and
 * {@link mayManageComment} withhold Edit and Delete from personal content while a company context is
 * active, so this refusal is the PAGE that was rendered before the context changed — the context lives
 * only in memory and a second tab pane can enter one after the row was drawn.
 */
export const actingAsCompanyOnOwnContentMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, ACTING_AS_COMPANY_ON_OWN_CONTENT)
    ? intl.formatMessage({
        id: "company.context.actingAsCompanyOnOwnContent",
        defaultMessage:
          "This content is your own, so changing it means leaving the company you are acting as. Choose “Leave company context”, then try again.",
      })
    : null;

/**
 * The caller's standing on the company one subject is filed under: `true` where they administer it,
 * `false` where they do not, and `null` where there is no company to administer at all.
 *
 * <b>Three states, not two.</b> "There is no company" and "there is one and I do not administer it"
 * are different facts and {@link companyFrozenCommentMessage} answers differently for them, so
 * collapsing the pair into a boolean would make a personal comment on a personal item look exactly
 * like a stranger's comment on a company's item.
 *
 * <p>Both a `SharedItem` and a `SharedItemComment` carry the `company` and `isAdminOfCompany` pair,
 * which is why one reader serves both.
 */
export type CompanyStanding = boolean | null;

export const companyStanding = (subject: ShareItemLike): CompanyStanding =>
  subject?.company ? Boolean(subject.isAdminOfCompany) : null;

/**
 * Which of the two frozen-company sentences a refused COMMENT write gets.
 *
 * <b>Why a comment needs the choice made for it, when an item does not.</b> Until concept v19 the
 * freeze reached a comment only through the company's own voice, so whoever met the refusal was an
 * admin of that company by construction and {@link companyFrozenMessage}'s repair advice always
 * applied. Concept v20 (a) (the company account model, Kevin 2026-09-10)
 * freezes EVERY comment on an inactive company's content — "auch kein **privater**, auch nicht von
 * seiner eigenen Autorin" — so the same refusal now reaches somebody with no connection to that
 * company at all. Telling them to reactivate it is an instruction they have no rights to follow.
 *
 * <b>Every company the write touches has to be administered, not just one.</b> A comment write can
 * implicate two: the company the comment is filed under, and the company the ITEM is filed under.
 * genesis raises one code for both and names neither, so spark cannot tell which of the two is the
 * dormant one — which means the repair advice is only certainly followable when the caller
 * administers all of them. An admin of company A commenting in A's name on inactive company B's post
 * therefore gets the bare fact: "reactivate the company" would point at a company they cannot touch.
 *
 * <p>And a write that touches NO company gets the reader sentence too. GENESIS001250 should not be
 * reachable there at all; if it arrives anyway, the honest answer is the fact without an instruction
 * naming a company nobody can find on the screen.
 *
 * @param touched the caller's standing on each company this write touches — see
 *                {@link CompanyStanding}. `null` entries are the sides that carry no company.
 */
export const companyFrozenCommentMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
  touched: readonly CompanyStanding[],
): string | null => {
  const companies = touched.filter((standing): standing is boolean => standing !== null);
  return companies.length > 0 && companies.every(Boolean)
    ? companyFrozenMessage(intl, error)
    : companyFrozenReaderMessage(intl, error);
};

export const itemTypeLabel = (t: string | null | undefined): string => {
  if (t === "PROJECT") return "Projekt";
  if (t === "POST") return "Post";
  return t ?? "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const creatorName = (c: any): string => {
  if (!c) return "";
  const parts = [c.name?.given, c.name?.family].filter(Boolean) as string[];
  if (parts.length) return parts.join(" ");
  return c.formattedName ?? "";
};

export const fmtCount = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  return v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, "") + "k" : String(v);
};

export const fiveStarRounded = (n: number | null | undefined): string =>
  (Math.round((Number(n) || 0) * 2) / 2).toFixed(1);
